'use client';

import { useCallback, useMemo, useRef, type RefObject } from 'react';

import { useTranslations } from 'next-intl';

import { ProxyFrame } from '@/components/ProxyFrame/ProxyFrame';
import { DEVICE_WIDTHS } from '@/types/feedback';
import { getDeviceLabel } from '@/utils/getDeviceLabel';

import {
  FRAME_GAP_NATIVE,
  PREVIEW_DEVICES,
  PRIMARY_DEVICE,
  type CanvasFrameRefs,
} from './canvasConstants';
import { CanvasDeviceFrame } from './CanvasDeviceFrame';
import { useCanvasScale } from './useCanvasScale';

import type { CommentUserInfo, DevicePreset, Pin } from '@/types/feedback';

interface CanvasPageProps {
  targetUrl: string;
  frameRefs: RefObject<CanvasFrameRefs>;
  pins: Pin[];
  commenterInfoMap?: Record<string, CommentUserInfo>;
  pinMode: boolean;
  inspectOnly?: boolean;
  pinsVisible: boolean;
  scrollLocked: boolean;
  selectionDisabled: boolean;
  onFrameReady?: (device: DevicePreset) => void;
  onFrameLoading?: (device: DevicePreset) => void;
  showFrameSpinner?: boolean;
  frameContentVisible?: boolean;
  onPinAdded: (
    pin: Omit<Pin, 'label' | 'createdAt'>,
    viewportX: number,
    viewportY: number,
  ) => void;
  onPinMarkerClick: (
    pinId: string,
    viewportX: number,
    viewportY: number,
  ) => void;
  onPinMoved: (
    pinId: string,
    selector: string,
    offsetXPct: number,
    offsetYPct: number,
    fallbackXPct: number,
    fallbackYPct: number,
  ) => void;
  onScrolledToPin: (
    pinId: string,
    viewportX: number,
    viewportY: number,
  ) => void;
  onPageChanged?: (pagePath: string) => void;
  onDismissPopover?: () => void;
}

export function CanvasPage(props: CanvasPageProps) {
  const t = useTranslations();
  const containerRef = useRef<HTMLDivElement>(null);
  const scale = useCanvasScale(containerRef);

  const frameRefs = props.frameRefs;
  const { onFrameLoading, onFrameReady } = props;

  // ProxyFrame treats a changed onLoading callback as a new load. Keep one
  // callback per device so ordinary CanvasPage renders cannot restart loading.
  const frameLoadingHandlers = useMemo(
    () =>
      Object.fromEntries(
        PREVIEW_DEVICES.map((device) => [
          device,
          () => onFrameLoading?.(device),
        ]),
      ) as Record<DevicePreset, () => void>,
    [onFrameLoading],
  );
  const frameReadyHandlers = useMemo(
    () =>
      Object.fromEntries(
        PREVIEW_DEVICES.map((device) => [device, () => onFrameReady?.(device)]),
      ) as Record<DevicePreset, () => void>,
    [onFrameReady],
  );

  const handleScroll = useCallback(
    (sourceDevice: DevicePreset, scrollY: number) => {
      for (const device of PREVIEW_DEVICES) {
        if (device === sourceDevice) continue;
        frameRefs.current[device]?.syncScroll(scrollY);
      }
    },
    [frameRefs],
  );

  return (
    <div ref={containerRef} className="relative flex-1 overflow-auto px-6 py-8">
      <div
        className="flex items-start justify-center"
        style={{ gap: FRAME_GAP_NATIVE * scale }}
      >
        {PREVIEW_DEVICES.map((device) => {
          const devicePins = props.pins.filter(
            (p) => !p.devicePreset || p.devicePreset === device,
          );
          return (
            <CanvasDeviceFrame
              key={device}
              device={device}
              scale={scale}
              label={t('get-feedback-toolbar-deviceTitle', {
                label: getDeviceLabel(device, t),
                width: DEVICE_WIDTHS[device],
              })}
            >
              <ProxyFrame
                ref={(handle) => {
                  frameRefs.current[device] = handle;
                }}
                targetUrl={props.targetUrl}
                deviceWidth={DEVICE_WIDTHS[device]}
                device={device}
                pins={devicePins}
                commenterInfoMap={props.commenterInfoMap}
                pinMode={props.pinMode}
                inspectOnly={props.inspectOnly}
                pinsVisible={props.pinsVisible}
                scrollLocked={props.scrollLocked}
                selectionDisabled={props.selectionDisabled}
                onLoading={frameLoadingHandlers[device]}
                showLoadingOverlay={
                  device === PRIMARY_DEVICE ? props.showFrameSpinner : false
                }
                contentVisible={
                  device === PRIMARY_DEVICE
                    ? (props.frameContentVisible ?? true)
                    : true
                }
                onReady={frameReadyHandlers[device]}
                onPageChanged={
                  device === PRIMARY_DEVICE ? props.onPageChanged : undefined
                }
                onPinAdded={(partial, vx, vy) =>
                  props.onPinAdded({ ...partial, devicePreset: device }, vx, vy)
                }
                onPinMarkerClick={props.onPinMarkerClick}
                onPinMoved={props.onPinMoved}
                onScrolledToPin={props.onScrolledToPin}
                onScroll={(scrollY) => handleScroll(device, scrollY)}
                onDismissPopover={props.onDismissPopover}
              />
            </CanvasDeviceFrame>
          );
        })}
      </div>
    </div>
  );
}
